import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
  CartesianGrid
} from 'recharts';

import { Box, Typography, Tooltip as MUItooltip } from '@mui/material';
import { trackAnalyticsEvent } from '../../../../utils/clevertap';
import { useSelector } from 'react-redux';

const PerformanceBarChart = ({ unit, data, title = "Performance History" }) => {
  const userData = useSelector(state => state.user?.userData);
  
  const handleTooltipLoad = (quarter, sales) => {
    trackAnalyticsEvent('MY_BENEFIT_PERFORMANCE_HISTORY_TOOLTIP_LOAD', {
      manufacturer_id: userData?.manufacturer_id || '',
      quarter: quarter || '',
      sales_value: sales || 0,
      unit: unit || ''
    });
  };
  
  const paddedData = data.length === 1
    ? [
        { ...data[0] }, 
        { quarter: '', sales: 0, logo: '', club_level: '' },
        { quarter: '', sales: 0, logo: '', club_level: '' },
        { quarter: '', sales: 0, logo: '', club_level: '' },
        { quarter: '', sales: 0, logo: '', club_level: '' }
      ]
    : data;
    
  return (
    <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h2">
            {title}
        </Typography>
        <Box sx={{ display:'flex', justifyContent:'space-between', borderBottom: '1px solid #d9d9d9', paddingBottom: 1, marginBottom: 1 }}>
            <Typography variant="h3" sx={{ color: '#666', py: 0.3 }}>
                Average Sales ({unit})
            </Typography>
        </Box>
        {data.length === 0 && (
            <Box sx={{ textAlign: 'center', color: '#999', py: 2, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant='body1'>No history data available</Typography>
            </Box>
        )}
        {data.length > 0 && (
            <ResponsiveContainer width="100%" height={240}>
                <BarChart 
                    data={paddedData} 
                    margin={{ top: 60, right: 0, left: -15, bottom: 0 }}
                    barCategoryGap={data.length === 1 ? "10%" : "20%"}
                >
                    <XAxis dataKey="quarter" axisLine={false} tickLine={false} />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                    />
                    <CartesianGrid stroke="#d9d9d9" strokeDasharray="4 4" vertical={false} />
                    <Tooltip
                        cursor={{ fill: 'transparent' }}
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                            const current_value = payload[0].value;
                            if (current_value === 0 || !label) return null;
                            
                            // Track tooltip load event
                            handleTooltipLoad(label, current_value);
                            
                            const uom = unit || '';
                            return (
                                <Box
                                    style={{
                                        backgroundColor: '#995A00',
                                        color: '#fff',
                                        fontSize: '0.75rem',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
                                    }}>
                                    {`${current_value} ${uom}`}
                                </Box>
                            );
                            }
                            return null;
                        }}
                        />
                    
                    <Bar dataKey="sales" radius={[10, 10, 0, 0]} barSize={40}>
                        <LabelList
                            dataKey="club_level"
                            content={({ x, y, index }) => {
                                const entry = paddedData[index];
                                if (!entry?.logo || !entry?.club_level) return null;
                                return (
                                    <g transform={`translate(${x + 20}, ${y - 60})`}>
                                        <foreignObject width={100} height={100} x={-50} y={5}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Box component={'img'}
                                                src={entry.logo}
                                                alt={entry.club_level}
                                                sx={{ height: 30 }}
                                            />
                                            <Typography variant="labelS" fontSize={10}>
                                                {entry.club_level}
                                            </Typography>
                                        </Box>
                                        </foreignObject>
                                    </g>
                                );
                            }}
                        />
                        {paddedData.map((entry, index) => (
                            <Cell 
                                key={`cell-${index}`} 
                                fill={entry?.sales === 0 ? 'transparent' : '#B1D3DB'} 
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        )}
    </Box>
  );
};

export default PerformanceBarChart;
